import React, { useState, useEffect } from 'react';
import { Home } from 'lucide-react';
import sendUltrasonicDailyCheckEmail from './ultrasonicDailyCheckEmailService';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';
import SerialValidationModal from './SerialValidationModal';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import { getLondonDateKey } from '../utils/londonDate';

const CHECK_ITEMS = [
  {
    field: 'machineFreeFromDamage',
    label: 'The Machine is free from damage that may have an impact on its readings validity?',
  },
  {
    field: 'batteryFreeFromDamage',
    label: 'The Machine battery is free from any dents/cracks or corrosion?',
  },
  {
    field: 'calibratedWithinTolerance',
    label: 'The Machine has been calibrated against the UKAS calibrated step block and is within 0.1mm of every step measurement?',
  },
];

const CALIBRATION_STEPS = [
  { field: 'reading5mm', label: '5mm', expected: 5 },
  { field: 'reading10mm', label: '10mm', expected: 10 },
  { field: 'reading15mm', label: '15mm', expected: 15 },
  { field: 'reading20mm', label: '20mm', expected: 20 },
  { field: 'reading25mm', label: '25mm', expected: 25 },
];

const EMPTY_READINGS = CALIBRATION_STEPS.reduce((acc, step) => {
  acc[step.field] = '';
  return acc;
}, {});

const READING_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;
const CALIBRATION_FAIL_MESSAGE =
  'The ultrasonic reader must be within 0.1mm of the calibration block. Please contact management.';

const isWithinTolerance = (rawValue, expected) => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return false;
  return Math.abs(value - expected) <= 0.1 + 1e-9;
};

const UltrasonicDailyCheckForm = ({ onBack, gateStatus, onCompleted }) => {
  const today = getLondonDateKey();
  const [formData, setFormData] = useState({
    inspectorName: '',
    date: today,
    machineFreeFromDamage: 'no',
    batteryFreeFromDamage: 'no',
    calibratedWithinTolerance: 'no',
    ...EMPTY_READINGS,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCalibrationFailModal, setShowCalibrationFailModal] = useState(false);

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData((prev) => (prev.inspectorName ? prev : { ...prev, inspectorName: name }));
  }, []);

  const alreadyCompleted = gateStatus?.completedToday;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    if (alreadyCompleted) {
      setSubmitMessage('Ultrasonic daily check has already been completed today.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.inspectorName.trim()) {
      setSubmitMessage('Unable to determine your name from login credentials. Please log in again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const missingReading = CALIBRATION_STEPS.some((step) => {
      const raw = String(formData[step.field] ?? '').trim();
      return raw === '' || raw === '.' || !Number.isFinite(Number(raw));
    });
    if (missingReading) {
      setSubmitMessage('Please enter a reading for every calibration block step.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const outOfTolerance = CALIBRATION_STEPS.some(
      (step) => !isWithinTolerance(formData[step.field], step.expected)
    );
    if (outOfTolerance) {
      setShowCalibrationFailModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await sendUltrasonicDailyCheckEmail(formData);
      if (typeof onCompleted === 'function') {
        await onCompleted();
      }
      setFormData({
        inspectorName: getCurrentUserDisplayName(),
        date: getLondonDateKey(),
        machineFreeFromDamage: 'no',
        batteryFreeFromDamage: 'no',
        calibratedWithinTolerance: 'no',
        ...EMPTY_READINGS,
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.message?.includes('already been completed')) {
        setSubmitMessage('Ultrasonic daily check has already been completed today.');
      } else if (error.message?.includes('calibration block')) {
        setShowCalibrationFailModal(true);
      } else {
        setSubmitMessage('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (alreadyCompleted) {
    return (
      <PVSPageLayout className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30 p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-3">Ultrasonic Daily Check</h1>
            <p className="text-pvs-text-muted mb-2">
              Today&apos;s check has already been completed
              {gateStatus?.completedBy ? ` by ${gateStatus.completedBy}` : ''}.
            </p>
            <p className="text-sm text-amber-300 mb-6">
              This form will be available again after midnight.
            </p>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md text-sm font-medium text-pvs-accent hover:bg-pvs-accent/10"
            >
              <Home className="h-4 w-4" />
              Back to Forms
            </button>
          </div>
        </div>
      </PVSPageLayout>
    );
  }

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Ultrasonic Daily Check</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-UDC-001 | Version: 2.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Ultrasonic daily calibration check</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {submitMessage && (
              <div className="p-5 rounded-lg border-2 shadow-md bg-red-900/30 text-red-400 border-red-500/30">
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Inspector Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Name</label>
                  <input
                    type="text"
                    name="inspectorName"
                    value={formData.inspectorName}
                    readOnly
                    className="block w-full pvs-input opacity-80 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    readOnly
                    className="block w-full pvs-input opacity-80 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Calibration Block Readings</h2>
              <p className="text-sm text-pvs-text-dim mb-4">
                Enter the ultrasonic reading for each step. Each value must be within 0.1mm of the block thickness.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse">
                  <thead>
                    <tr>
                      {CALIBRATION_STEPS.map((step) => (
                        <th
                          key={step.field}
                          className="border border-pvs-border px-2 py-3 text-sm font-semibold text-pvs-accent text-center bg-pvs-accent/5"
                        >
                          {step.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {CALIBRATION_STEPS.map((step) => {
                        const raw = String(formData[step.field] ?? '').trim();
                        const hasValue = raw !== '' && raw !== '.';
                        const invalid = hasValue && !isWithinTolerance(raw, step.expected);
                        return (
                          <td key={step.field} className="border border-pvs-border px-2 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              name={step.field}
                              value={formData[step.field]}
                              onChange={(e) => {
                                const next = e.target.value.replace(/[^\d.]/g, '');
                                if (next === '' || READING_PATTERN.test(next)) {
                                  setFormData((prev) => ({ ...prev, [step.field]: next }));
                                }
                              }}
                              onBlur={(e) => {
                                const value = String(e.target.value ?? '').trim();
                                if (value === '' || value === '.') return;
                                if (!isWithinTolerance(value, step.expected)) {
                                  setShowCalibrationFailModal(true);
                                }
                              }}
                              placeholder={`${step.expected}.0`}
                              className={`block w-full pvs-input text-center ${
                                invalid ? 'border-red-500 focus:border-red-500' : ''
                              }`}
                              aria-label={`${step.label} calibration reading`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Daily Checks</h2>
              <div className="space-y-4">
                {CHECK_ITEMS.map(({ field, label }) => (
                  <div key={field} className="flex items-center justify-between gap-4">
                    <label className="text-sm font-medium text-pvs-text-muted flex-1">{label}</label>
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <span className={`text-sm font-medium ${formData[field] === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, [field]: prev[field] === 'yes' ? 'no' : 'yes' }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${
                          formData[field] === 'yes' ? 'bg-green-600' : 'bg-red-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData[field] === 'yes' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`text-sm font-medium ${formData[field] === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                <Home className="h-4 w-4" />
                Back to Forms
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Ultrasonic Daily Check'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <SerialValidationModal
        isOpen={showCalibrationFailModal}
        title="Calibration Check Failed"
        message={CALIBRATION_FAIL_MESSAGE}
        onClose={() => setShowCalibrationFailModal(false)}
      />

      <FormSuccessModal
        isOpen={showSuccessModal}
        formName="Ultrasonic Daily Check"
        onClose={() => {
          setShowSuccessModal(false);
          onBack?.();
        }}
      />
    </PVSPageLayout>
  );
};

export default UltrasonicDailyCheckForm;
